import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { Organization } from '../data-models/organization.model';
import { OrganizationRequest } from '../request-models/organization-request.model';
import { OrganizationMember } from '../response-models/organization-member.model';
import { OrganizationPilot } from '../response-models/organization-pilot.model';
import { toOrganizationCreateRequest, toOrganizationUpdateRequest } from '../utils/organization-request-utils';
import { OrganizationContextService } from '../services/organization-context.service';
import { isRecord } from '../utils/is-record';
import { nullableNumber } from '../utils/wire-value-utils';

/**
 * Organizations: `/organizations[/:id]` and `/organizations/mine` (hub `user-organizations-controller.ts`),
 * plus the member and pilot summaries under them (`/organizations/:id/members`, `/organizations/mine/pilots`).
 * Drones live in `DroneService`. Failures reject with an `ApiError`.
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationService extends BaseService<Organization> {
  private organizationContext = inject(OrganizationContextService);

  constructor() {
    super('organizations', Organization);
  }

  /**
   * Create a new organization and make it the current organization context.
   */
  async createOrganization(organizationData: OrganizationRequest): Promise<Organization> {
    const created = await firstValueFrom(this.create(toOrganizationCreateRequest(organizationData)));
    if (!created) {
      throw new Error('Failed to create organization');
    }
    this.organizationContext.setCurrentOrganization(created.id);
    return created;
  }

  /**
   * The current user's organization (owned first, else the first membership), or `null` for a user
   * without one. Also sets the organization context when found.
   */
  async getMyOrganization(): Promise<Organization | null> {
    const organization = await firstValueFrom(this.get('mine'));
    if (organization) {
      this.organizationContext.setCurrentOrganization(organization.id);
    }
    return organization;
  }

  async getOrganization(id: string): Promise<Organization> {
    const organization = await firstValueFrom(this.get(id));
    if (!organization) {
      throw new Error('Failed to get organization');
    }
    return organization;
  }

  /**
   * Update an organization (owner only). Sends only the writable fields that are set; `null` clears a field.
   */
  async updateOrganization(id: string, organizationData: OrganizationRequest): Promise<Organization> {
    const updated = await firstValueFrom(this.update(id, toOrganizationUpdateRequest(organizationData)));
    if (!updated) {
      throw new Error('Failed to update organization');
    }
    return updated;
  }

  /** Members of an organization: `GET /organizations/:id/members`. */
  async getOrganizationMembers(orgId: string): Promise<OrganizationMember[]> {
    return firstValueFrom(
      this.request('GET', { id: orgId, path: 'members' }, data => (Array.isArray(data) ? (data as OrganizationMember[]) : []))
    );
  }

  /** Members of the current organization. */
  async getMyOrganizationMembers(): Promise<OrganizationMember[]> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      throw new Error('No organization selected');
    }
    return this.getOrganizationMembers(orgId);
  }

  /** Pilots with valid Part 107 and state applicator certifications: `GET /organizations/:id/pilots`. */
  async getOrganizationPilots(orgId: string): Promise<OrganizationPilot[]> {
    return this.getPilots(orgId);
  }

  /** Pilots of the current user's organization: `GET /organizations/mine/pilots` (any member may call it). */
  async getMyOrganizationPilots(): Promise<OrganizationPilot[]> {
    return this.getPilots('mine');
  }

  /**
   * Coordinates for an address, checked against the organization: `GET /organizations/:orgId/geocode`
   * (owner or chief supervisor). An address the hub can't place rejects with an `ApiError` (status 422).
   */
  async getOrganizationCoordinates(
    orgId: string,
    address: { streetAddressLine1: string; city: string; state: string; zipCode?: string }
  ): Promise<{ latitude: number; longitude: number }> {
    const coordinates = await firstValueFrom(
      this.request('GET', { id: orgId, path: 'geocode', query: { ...address } }, toCoordinates)
    );
    if (!coordinates) {
      throw new Error('Failed to get organization coordinates');
    }
    return coordinates;
  }

  /** `GET /organizations/{orgIdOrMine}/pilots`. */
  private getPilots(orgIdOrMine: string): Promise<OrganizationPilot[]> {
    return firstValueFrom(
      this.request('GET', { id: orgIdOrMine, path: 'pilots' }, data => (Array.isArray(data) ? (data as OrganizationPilot[]) : []))
    );
  }
}

/** The hub's `{ latitude, longitude }`, or `null` unless both are numbers (zero is kept). */
function toCoordinates(data: unknown): { latitude: number; longitude: number } | null {
  const latitude = isRecord(data) ? nullableNumber(data['latitude']) : undefined;
  const longitude = isRecord(data) ? nullableNumber(data['longitude']) : undefined;
  return latitude != null && longitude != null ? { latitude, longitude } : null;
}
